import { creditCardFiConfig } from '@axo/config/registry/presets/credit-card-fi.config';
import { creditCardNoConfig } from '@axo/config/registry/presets/credit-card-no.config';
import { MarketCountryCode } from '@axo/shared/types';

export type CreditCardAmountLimits = {
  min?: number;
  max?: number;
  step?: number;
  default: number;
};

/**
 * Agent-facing credit limit bounds. Mirrors UNSECURED_LOAN_AMOUNT_LIMITS and
 * SECURED_LOAN_AMOUNT_LIMITS in forms/createApplication/constants.ts.
 *
 * Both markets read their bounds from the customer-facing presets (MV-3339).
 * Only Norway's `default` diverges: credit-card-no.config.ts specifies 70_000,
 * and MV-3339 requires the Norwegian agent default to stay at 30_000.
 *
 * Partial<Record> rather than a total Record, unlike the two maps above: the
 * Eligible products panel is gated to NO and FI, so SE and DK have no
 * product-backed numbers to state.
 *
 * NOTE: min/max/step are passed to the dialog's NumericInput but are NOT
 * enforced — Blueprint does not clamp typed input by default and the ticket
 * asks for no validation. See specs/MV-3339-credit-card-panel-fi/spec.md.
 */
export const CREDIT_CARD_AMOUNT_LIMITS = {
  [MarketCountryCode.Norway]: {
    ...creditCardNoConfig.config.amount,
    default: 30_000, // ACE's established agent default (MV-2208) — deliberately not credit-card-no.config.ts's 70_000
  },
  [MarketCountryCode.Finland]: { ...creditCardFiConfig.config.amount },
} satisfies Partial<Record<MarketCountryCode, CreditCardAmountLimits>>;

/**
 * `marketCountry` is widened to `string` because LoanApplication.MarketCountry
 * is typed `string`.
 *
 * The Norway fallback is unreachable in practice: the panel only renders when
 * the whitelist in ApplicationEditor matches NO or FI. It exists so that an
 * unmapped market reaching the dialog behaves exactly as ACE did before MV-3339.
 */
export const resolveCreditCardAmountLimits = (
  marketCountry: string | undefined
): CreditCardAmountLimits =>
  CREDIT_CARD_AMOUNT_LIMITS[
    marketCountry as keyof typeof CREDIT_CARD_AMOUNT_LIMITS
  ] ?? CREDIT_CARD_AMOUNT_LIMITS[MarketCountryCode.Norway];
